import { useEffect, useState } from 'react';
import { useFeature } from '../../lib/features';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Copy, LayoutGrid } from 'lucide-react';
import { useStore } from '../../store/StoreContext';
import { fetchMyTemplateStats, fetchTemplatesSummary, likeTemplate, unlikeTemplate } from '../../api/dataApi';
import { queryClient, queryKeys } from '../../lib/queryClient';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { AppCard } from '../../components/ui/AppCard';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { Toast } from '../../components/ui/Toast';
import { useAvatarImageUrl } from '../../components/ui/AvatarView';

export function PublicTemplateScreen() {
  const cloneOn = useFeature('channels.clone');
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { templates, isTemplateOwner, isLoggedIn, applyTemplateStats, cloneTemplate } = useStore();
  const template = templates.find((t) => t.id === templateId);
  const [busy, setBusy] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [error, setError] = useState('');
  const [channelsUsing, setChannelsUsing] = useState<number | null>(null);
  const imageUrl = useAvatarImageUrl(template?.avatarImageKey);

  // "Used in N channels" - pulled from the system-wide roll-up
  // (GET /stats/templates/summary), same source the Templates list uses.
  useEffect(() => {
    if (!template) return;
    let cancelled = false;
    fetchTemplatesSummary().then((s) => {
      if (!cancelled) setChannelsUsing(s.byTemplate[template.nickname]?.channels ?? 0);
    });
    return () => {
      cancelled = true;
    };
  }, [template?.nickname]);

  // See ChannelDetailsScreen's identical effect - the public `likes` count
  // already comes with the template (dataApi.ts's fetchTemplate merges in
  // GET /stats/template/{nickname}); "did *I* like this" needs its own
  // authenticated call.
  useEffect(() => {
    if (!template || !isLoggedIn) return;
    fetchMyTemplateStats(template.nickname).then((stats) => applyTemplateStats(template.id, stats));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template?.id, template?.nickname, isLoggedIn]);

  const toggleLike = async () => {
    if (!template) return;
    if (!isLoggedIn) {
      setError('Log in to like a schema.');
      return;
    }
    setBusy(true);
    try {
      // See ChannelDetailsScreen.tsx's toggleLike for the identical
      // rationale on routing through queryClient.query with the action
      // baked into the key, and retry: 0 (a write, not a read).
      const result = template.liked
        ? await queryClient.query({ queryKey: queryKeys.templateLike(template.nickname, 'unlike'), queryFn: () => unlikeTemplate(template.nickname), staleTime: 0, retry: 0 })
        : await queryClient.query({ queryKey: queryKeys.templateLike(template.nickname, 'like'), queryFn: () => likeTemplate(template.nickname), staleTime: 0, retry: 0 });
      applyTemplateStats(template.id, result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update like.');
    } finally {
      setBusy(false);
    }
  };

  // Makes the current user their own editable copy of this template (see
  // dataApi's cloneTemplate) and drops them on its edit screen to rename/
  // tweak it. Available to anyone logged in who isn't already the owner.
  const clone = async () => {
    if (!template) return;
    if (!isLoggedIn) {
      setError('Log in to clone a schema.');
      return;
    }
    setCloning(true);
    try {
      const created = await cloneTemplate(template, 'public_template');
      navigate(`/templates/${created.id}/edit`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to clone this schema.');
    } finally {
      setCloning(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <ScreenHeader
        title={template?.isPublic === false ? 'Schema' : 'Public schema'}
        action={
          template && isTemplateOwner(template) ? (
            <Link to={`/templates/${template.id}/edit`}>
              <Button variant="bordered">Edit</Button>
            </Link>
          ) : undefined
        }
      />

      {!template ? (
        <EmptyState icon={LayoutGrid} title="Schema missing" subtitle="The schema is unavailable." />
      ) : (
        <>
          <AppCard className="flex flex-col gap-2">
            {imageUrl ? (
              <img src={imageUrl} alt="" className="w-16 h-16 rounded-2xl object-cover" />
            ) : (
              <span className="text-4xl">{template.emoji}</span>
            )}
            <span className="text-2xl font-bold">{template.title}</span>
            <span className="text-black/50 dark:text-white/50">{template.nickname}</span>
            <span>{template.signals.length} signal{template.signals.length === 1 ? '' : 's'}</span>
            <span>Likes: {template.likes}</span>
            <span>
              {channelsUsing === null
                ? 'Usage: …'
                : channelsUsing === 0
                  ? 'Not used in any channel yet'
                  : `Used in ${channelsUsing} channel${channelsUsing === 1 ? '' : 's'}`}
            </span>
          </AppCard>

          <div className="flex gap-3">
            <Button onClick={toggleLike} disabled={busy} full>
              {template.liked ? 'Unlike' : 'Like'}
            </Button>
            {!isTemplateOwner(template) && cloneOn && (
              <Button variant="bordered" onClick={clone} loading={cloning} full>
                <Copy size={16} />
                Clone
              </Button>
            )}
          </div>

          <AppCard className="flex flex-col gap-3">
            <span className="font-semibold">Signals</span>
            {template.signals.map((signal) => (
              <div key={signal.id} className="flex items-center gap-3">
                {/* Same medallion look as a sent signal message (see
                    MessagesScreen.tsx's SignalMedallion) - the color is
                    now shown by the badge itself, not printed as a raw
                    hex string. */}
                <span
                  className="w-10 h-10 rounded-full flex items-center justify-center text-lg shrink-0 border-2"
                  style={{ background: `${signal.colorHex}1f`, borderColor: signal.colorHex }}
                >
                  {signal.emoji}
                </span>
                <span className="flex-1">
                  {signal.name}
                  {signal.value && <span className="text-black/50 dark:text-white/50"> · {signal.value}</span>}
                </span>
              </div>
            ))}
          </AppCard>
        </>
      )}

      {error && <Toast message={error} onDismiss={() => setError('')} />}
    </div>
  );
}
